import {
  RenderAudioEventTracker,
  createEmptyRenderAudioEvents,
  type RenderAudioEvents
} from "./audio_events";
import { extractId, findLocalEntity, updateCamera, worldToScreen } from "./camera";
import { clearCanvas, drawBackgroundGrid, drawLeaderCrown } from "./drawing";
import {
  createEntityFadeState,
  renderEntities,
  resetEntityFadeState
} from "./entities";
import { renderGameplayUi, type GameplayUiModel } from "./ui";
import type { Camera, GameSnapshot, SnapshotEntity } from "./types";

export type { GameSnapshot, LeaderboardEntry, SnapshotEntity } from "./types";
export type { RenderAudioEvents } from "./audio_events";

export type RendererOptions = {
  canvas: HTMLCanvasElement;
};

export class Renderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly audioEvents = new RenderAudioEventTracker();
  private readonly entityFadeState = createEntityFadeState();
  private readonly camera: Camera = {
    x: 0,
    y: 0,
    zoom: 1.45
  };

  public constructor(options: RendererOptions) {
    this.canvas = options.canvas;

    const context = this.canvas.getContext("2d");

    if (!context) {
      throw new Error("2D canvas context is not available");
    }

    this.context = context;
    this.resize();
  }

  public resize(): void {
    const width = Math.max(1, Math.floor(this.canvas.clientWidth || window.innerWidth));
    const height = Math.max(1, Math.floor(this.canvas.clientHeight || window.innerHeight));
    const ratio = window.devicePixelRatio || 1;

    this.canvas.width = Math.floor(width * ratio);
    this.canvas.height = Math.floor(height * ratio);
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  public getPlayerScreenPosition(snapshot: GameSnapshot | null): { x: number; y: number } {
    const width = this.viewportWidth();
    const height = this.viewportHeight();

    if (!snapshot) {
      return {
        x: width * 0.5,
        y: height * 0.5
      };
    }

    updateCamera(this.camera, snapshot, { width, height });

    const local = findLocalEntity(snapshot);

    if (!local) {
      return {
        x: width * 0.5,
        y: height * 0.5
      };
    }

    return worldToScreen(local.position.x, local.position.y, this.camera, width, height);
  }

  public render(snapshot: GameSnapshot | null, ui: GameplayUiModel): RenderAudioEvents {
    const width = this.viewportWidth();
    const height = this.viewportHeight();

    clearCanvas(this.context, width, height);

    if (!snapshot) {
      resetEntityFadeState(this.entityFadeState);
      drawBackgroundGrid(this.context, width, height, this.camera);
      this.audioEvents.reset();
      return createEmptyRenderAudioEvents();
    }

    updateCamera(this.camera, snapshot, { width, height });

    const audioEvents = this.audioEvents.collect(snapshot, this.camera, width, height);

    drawBackgroundGrid(
      this.context,
      width,
      height,
      this.camera,
      snapshot.arena_width ?? snapshot.arenaWidth ?? 6400,
      snapshot.arena_height ?? snapshot.arenaHeight ?? 6400
    );

    audioEvents.visibleDamage = renderEntities(
      this.context,
      snapshot.entities as SnapshotEntity[],
      this.camera,
      width,
      height,
      this.entityFadeState,
      snapshot.local_player_id ?? snapshot.localPlayerId
    );

    renderGameplayUi(this.context, width, height, snapshot, ui);
    renderLeaderPointer(this.context, width, height, snapshot, this.camera);

    return audioEvents;
  }

  private viewportWidth(): number {
    return Math.floor(this.canvas.width / (window.devicePixelRatio || 1));
  }

  private viewportHeight(): number {
    return Math.floor(this.canvas.height / (window.devicePixelRatio || 1));
  }
}

function renderLeaderPointer(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  snapshot: GameSnapshot,
  camera: Camera
): void {
  const leaderPosition = snapshot.leader_position ?? snapshot.leaderPosition ?? null;
  const leaderLevel = snapshot.leader_level ?? snapshot.leaderLevel ?? null;

  if (!leaderPosition || typeof leaderLevel !== "number" || leaderLevel < 2) {
    return;
  }

  const local = findLocalEntity(snapshot);
  const leaderId = extractId(snapshot.leader_player_id ?? snapshot.leaderPlayerId);
  const localOwner = extractId(local?.owner);

  if (leaderId !== null && localOwner !== null && leaderId === localOwner) {
    return;
  }

  const leaderScreen = worldToScreen(
    leaderPosition.x,
    leaderPosition.y,
    camera,
    width,
    height
  );
  const visibilityPadding = 22;

  if (
    leaderScreen.x >= -visibilityPadding &&
    leaderScreen.x <= width + visibilityPadding &&
    leaderScreen.y >= -visibilityPadding &&
    leaderScreen.y <= height + visibilityPadding
  ) {
    return;
  }

  const centerX = width * 0.5;
  const centerY = height * 0.5;
  const directionX = leaderScreen.x - centerX;
  const directionY = leaderScreen.y - centerY;
  const length = Math.hypot(directionX, directionY);

  if (!Number.isFinite(length) || length <= 0.001) {
    return;
  }

  const unitX = directionX / length;
  const unitY = directionY / length;
  const layoutScale = clamp(Math.min(width, height) / 720, 0.72, 1);
  const margin = Math.round(clamp(34 * layoutScale, 24, 36));
  const anchor = intersectViewportEdge(centerX, centerY, unitX, unitY, width, height, margin);
  const angle = Math.atan2(unitY, unitX);
  const arrowSize = Math.round(clamp(18 * layoutScale, 13, 18));
  const crownSize = Math.round(clamp(10 * layoutScale, 8, 11));
  const crownDistance = arrowSize * 1.15;

  context.save();
  context.translate(anchor.x, anchor.y);

  drawPointerTriangle(context, angle, arrowSize);

  drawLeaderCrown(
    context,
    -unitX * crownDistance,
    -unitY * crownDistance + crownSize * 0.72,
    crownSize
  );

  context.restore();
}

function intersectViewportEdge(
  centerX: number,
  centerY: number,
  unitX: number,
  unitY: number,
  width: number,
  height: number,
  margin: number
): { x: number; y: number } {
  const halfWidth = Math.max(1, width * 0.5 - margin);
  const halfHeight = Math.max(1, height * 0.5 - margin);
  const scaleX =
    Math.abs(unitX) > 0.0001
      ? halfWidth / Math.abs(unitX)
      : Number.POSITIVE_INFINITY;
  const scaleY =
    Math.abs(unitY) > 0.0001
      ? halfHeight / Math.abs(unitY)
      : Number.POSITIVE_INFINITY;
  const distance = Math.min(scaleX, scaleY);

  return {
    x: centerX + unitX * distance,
    y: centerY + unitY * distance
  };
}

function drawPointerTriangle(
  context: CanvasRenderingContext2D,
  angle: number,
  size: number
): void {
  context.save();
  context.rotate(angle);
  context.beginPath();
  context.moveTo(size, 0);
  context.lineTo(-size * 0.62, -size * 0.72);
  context.lineTo(-size * 0.38, 0);
  context.lineTo(-size * 0.62, size * 0.72);
  context.closePath();
  context.fillStyle = "#050505";
  context.fill();
  context.lineWidth = Math.max(1, size * 0.12);
  context.strokeStyle = "rgba(255,255,255,0.88)";
  context.stroke();
  context.restore();
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
